import React, { Component, createRef } from 'react';
import ReactDOM from 'react-dom';
import { FixedSizeList, VariableSizeList } from 'react-window';
import PropTypes from 'prop-types';

import kebabCase from 'lodash/kebabCase';

import { Tumbler } from './Tumbler';
import { ProgressCircle } from './ProgressCircle';
import { Icon } from './Icon';
import { Tooltip } from './Tooltip';
import { isEqual } from '../utils/compare';
import {
  moveItem,
  filterItems,
  getItemClasses,
  updateStateOfOptions,
  makeStateOfOptions,
  traverseState,
  normalizeValue,
  cloneOptions,
} from './select/helpers';
import { SortableContainer } from './select/SortableContainer';
import { SortableItem } from './select/SortableItem';
import { WithErrorPlaceholderImage } from './select/WithErrorPlaceholderImage';
import { Button } from './Button';

import '../scss/_select.scss';
import { uniqueId } from 'lodash';

const PADDING_STEP = 20;

const BtnWrapper = ({ children, className, tooltipParams, handleClick }) => {
  return tooltipParams ? (
    <Tooltip className={className} handleClick={handleClick} {...tooltipParams}>
      {children}
    </Tooltip>
  ) : (
    <div className={className} onClick={handleClick}>
      {children}
    </div>
  );
};

export class Select extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      search: '',
      tumblerIsActive: props.tumblerIsActive,
      withGroups: props.options.some((item) => item.isGroupHeader),
      stateOfOptions: makeStateOfOptions(props.options, props.value, props.dnd),
      isSorting: false,
      tabsModeIsInclude: true,
    };
    this.virtualizedVariableSizeListRef = React.createRef();
  }

  selectWrapperRef = createRef();

  _selectList = null;

  shouldComponentUpdate(nextProps, nextState) {
    return !(
      isEqual(
        [
          'value',
          'options',
          'isMulti',
          'multiPlaceholder',
          'allSelectable',
          'isSearchable',
          'hideValueOnFocus',
          'onChange',
          'showControlLabel',
          'tumbler',
          'dnd',
          'onTumblerChange',
          'label',
          'labelImageSrc',
          'reactLabel',
          'placeholder',
          'className',
          'isOpen', // sets isOpen state through prop
          'toggleOpenProp', // changes isOpen prop value
          'ignoredCloseClickElement', // prevents select from closing when clicking on outer element that changes isOpen prop
          'isAlwaysOpen',
          'btnOnClick',
          'isOptionsLoading',
          'portalClassName',
        ],
        this.props,
        nextProps,
      ) &&
      isEqual(
        [
          'isOpen',
          'search',
          'stateOfOptions',
          'tumblerIsActive',
          'targetDropIndex',
          'startDragIndex',
          'isSorting',
          'tabsModeIsInclude',
        ],
        this.state,
        nextState,
      )
    );
  }

  componentDidMount() {
    window.addEventListener('click', this.windowClickHandler);
    this.setOpenStateThroughProp();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !isEqual(['value', 'options', 'isMulti', 'dnd'], this.props, prevProps) ||
      !isEqual(['isSorting'], this.state, prevState)
    ) {
      this.setState({
        withGroups: this.props.options.some((item) => item.isGroupHeader),
        stateOfOptions: makeStateOfOptions(this.props.options, this.props.value, this.props.dnd),
      });
      if (
        this.props.options !== prevProps.options &&
        typeof this.props.virtualizationProps?.itemSize !== 'number' &&
        this.virtualizedVariableSizeListRef.current
      ) {
        this.virtualizedVariableSizeListRef.current.resetAfterIndex(0);
      }
    }
    if (prevProps.tumblerIsActive !== this.props.tumblerIsActive) {
      this.setState({ tumblerIsActive: this.props.tumblerIsActive });
    }
    if (this.state.isOpen && this.state.isOpen !== prevState.isOpen) {
      if (this._selectList) {
        this._selectList.classList.add(this.props.scrollableClassName || '_scrollable');
      }
      this.searchInput && this.searchInput.focus(); // has isOpen state changed to true, then focus search input
    }

    // If state.isOpen was changed, then props.isOpen is not yet updated here.
    // This function should be fired by props.isOpen change.
    if (prevState.isOpen === this.state.isOpen) {
      this.setOpenStateThroughProp(prevProps);
    }

    // clear filter after drop-down was closed
    if (!this.state.isOpen && prevState.isOpen) {
      this.setState({ search: '' });

      // pass '' to custom search handler
      if (this.props.changeSearchHandler) {
        this.props.changeSearchHandler('');
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.windowClickHandler);
  }

  getXYCoords() {
    const coord = this.selectWrapperRef?.current?.getBoundingClientRect?.() ?? {};
    return {
      top: coord?.bottom,
      left: coord?.left,
      width: coord?.width,
      zIndex: 9999, // We want it higher - refactor in future to have each popup component i.e tooltip to have a priority z-index.
    };
  }

  // opens/closes select clicking on external element
  setOpenStateThroughProp = (prevProps) => {
    // if isOpen prop is set and not equal to isOpen state
    if (typeof this.props.isOpen === 'boolean' && this.props.isOpen !== this.state.isOpen) {
      this.setState({ isOpen: this.props.isOpen });
      // prevents closing through windowClickHandler when clicking on external el that changes isOpen prop
      this.ignoredCloseClickElement = this.props.ignoredCloseClickElement;
    }
  };

  windowClickHandler = ({ target }) => {
    if (
      !this.state.isOpen ||
      target.closest('.js-select') === this.select ||
      // prevent closing if el added as ignored
      (this.ignoredCloseClickElement && target === this.ignoredCloseClickElement)
    ) {
      return;
    }
    this.setState({ isOpen: false });
    // changes isOpen prop when isOpen state is changed
    this.props.toggleOpenProp && this.props.toggleOpenProp(false);
  };

  toggleSelect = () => {
    const { btnOnClick } = this.props;

    const xycoords = this.getXYCoords();
    this.setState({
      xycoords,
    });
    if (btnOnClick) {
      btnOnClick();
    } else {
      const { isOpen } = this.state;
      this.setState({ isOpen: !isOpen });
      // changes isOpen prop when isOpen state is changed
      this.props.toggleOpenProp && this.props.toggleOpenProp(!this.state.isOpen);
    }
  };

  setStateOptions(state, item, fromDeselectedAll) {
    const { withConfirmation, onChange } = this.props;
    if (onChange) {
      let values = [];
      const callback = (item) => {
        if (item.isActive) values.push(item);
      };
      traverseState(state, callback);
      if (!this.props.isMulti) {
        values = values.length ? values[0] : null;
      }
      // add fromDeselectedAll for lazyLoad select
      onChange(normalizeValue(values), item, fromDeselectedAll);
    }
    if (!withConfirmation) {
      this.setState({ stateOfOptions: state });
    }
  }

  handleChangeSearch = ({ target }) => {
    this.setState({ search: target.value });
  };

  toggleTumbler = () => {
    this.props.onTumblerChange();
  };

  handleSelectedOption = (item) => (e) => {
    const {
      isMulti,
      withTabs,
      maxSelectedLimit,
      value,
      onExceededMaxLimit,
      virtualizationProps,
    } = this.props;
    const { stateOfOptions, tabsModeIsInclude } = this.state;

    if (virtualizationProps) {
      e.stopPropagation();
    }

    if (value && maxSelectedLimit && !item.isActive && value.length >= maxSelectedLimit) {
      onExceededMaxLimit && onExceededMaxLimit();
      return;
    }

    if (withTabs) {
      if (!item.isActive) {
        item = Object.assign(item, tabsModeIsInclude ? { included: true } : { excluded: true });
      }
    }

    const newState = updateStateOfOptions(stateOfOptions, item, isMulti);
    this.setStateOptions(newState, item);

    if (!isMulti) {
      this.toggleSelect();
    }
  };

  allSelected = () => {
    const { totalRecords, searchByValue, wildcardSearch } = this.props;
    const { stateOfOptions, search } = this.state;
    if (!stateOfOptions.length) {
      return false;
    }
    let allSelected = true;
    const callback = (item) => {
      if (!item.isActive) allSelected = false;
    };
    traverseState(filterItems(stateOfOptions, search, searchByValue, wildcardSearch), callback);
    return totalRecords ? allSelected && stateOfOptions.length === totalRecords : allSelected;
  };

  allVisibleItemsSelected = () => {
    const { searchByValue, wildcardSearch } = this.props;
    const { stateOfOptions, search } = this.state;
    if (!stateOfOptions.length) {
      return false;
    }
    let allSelected = true;
    const callback = (item) => {
      if (!item.isActive) allSelected = false;
    };
    traverseState(filterItems(stateOfOptions, search, searchByValue, wildcardSearch), callback);
    return allSelected;
  };

  deselectAll = () => {
    const { stateOfOptions } = this.state;
    const cloneState = cloneOptions(stateOfOptions);
    const callback = (item) => {
      if (!item.alwaysEnabled) {
        item.isActive = false;
      }
    };
    traverseState(cloneState, callback);
    this.setStateOptions(cloneState, null, true);
  };

  selectAll = () => {
    const { searchByValue, wildcardSearch, onSelectAll, withTabs } = this.props;
    const { stateOfOptions, search, tabsModeIsInclude } = this.state;
    const clonedState = cloneOptions(stateOfOptions);
    const cloneState = filterItems(clonedState, search, searchByValue, wildcardSearch);
    const callback = (item) => {
      item.isActive = true;
      if (withTabs) {
        Object.assign(item, tabsModeIsInclude ? { included: true } : { excluded: true });
      }
    };
    traverseState(cloneState, callback);
    this.setStateOptions(cloneState);
    if (onSelectAll) {
      onSelectAll();
    }
  };

  onSortStart = () => {
    this.setState({ isSorting: true });
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const newValues = moveItem(this.state.stateOfOptions, oldIndex, newIndex);
    this.setStateOptions(newValues);
    this.setState({
      targetDropIndex: null,
      startDragIndex: null,
      isSorting: false,
    });
  };

  handleClearValue = (e) => {
    e.stopPropagation();
    this.deselectAll();
    if (this.props.onClearAll) {
      this.props.onClearAll();
    }
  };

  renderItem = (item, i, style = {}) => {
    const {
      isMulti,
      levelPadding,
      dnd,
      withTabs,
      isReadOnly = false,
      virtualizationProps,
    } = this.props;
    const { targetDropIndex, startDragIndex, tabsModeIsInclude } = this.state;

    let additionalProps = {
      style: {},
    };
    const padd = (item.deep || 0) * (levelPadding || PADDING_STEP);
    if (padd !== 0) {
      additionalProps.style.paddingLeft = padd;
    }

    if (virtualizationProps) {
      const itemHeight =
        typeof virtualizationProps.itemSize === 'number'
          ? virtualizationProps.itemSize
          : virtualizationProps.itemSize(item);
      const virtualizationStyles = {
        ...style,
        height: `${itemHeight}px`,
        maxHeight: `${itemHeight}px`,
      };
      additionalProps.style = { ...additionalProps.style, ...virtualizationStyles };
    }

    if (!item.isGroupHeader && !item.alwaysEnabled) {
      additionalProps.onClick = this.handleSelectedOption(item);
    }

    // to make clickable group header bold the checking is needed:
    // - if option is not a child of other option and have its own children
    // - and also if no reactLabel is used since it has its own markup
    const isClickableGroupHeader =
      !item.reactLabel && item.deep === 0 && item.options && item.options.length;

    const { isActive, draggableGroup, lastFirstGroup, included, excluded } = item;
    let { isDisable = false } = item;

    if (withTabs) {
      isDisable = tabsModeIsInclude ? !!excluded : !!included;
    }

    let className = getItemClasses(item, { isActive, isDisable, tabsModeIsInclude, isReadOnly });

    if (dnd && isActive && isMulti) {
      const indexItem = i;
      className = getItemClasses(item, { isActive, indexItem, startDragIndex, targetDropIndex });
    }

    if (isClickableGroupHeader) {
      className += ' _clickable-header';
    }
    if (item.deep === 0) {
      className += ' _header-option';
    }

    let separator = lastFirstGroup ? <div className="_separator" /> : null;
    const key = `${i}_${padd}_${item.value || uniqueId()}`;
    const clickHandler = isReadOnly ? { ...additionalProps, onClick: () => {} } : additionalProps;

    return dnd ? (
      <React.Fragment key={key}>
        <SortableItem
          index={i}
          item={item}
          isActive={isActive}
          disabled={!isActive}
          collection={draggableGroup ? `${draggableGroup}` : isActive ? 'active' : 'disabled'}
          className={className}
          {...additionalProps}
        />
        {separator}
      </React.Fragment>
    ) : (
      <div key={key} className={className} {...clickHandler}>
        <div className="select__option-label">
          {item.labelImageSrc && (
            <div className="select__option-image">
              <WithErrorPlaceholderImage src={item.labelImageSrc} />
            </div>
          )}
          {item.reactLabel || item.label}
        </div>
      </div>
    );
  };

  getRenderVirtualizedItem = (items) => ({ index, style }) => {
    return this.renderItem(items[index], index, style);
  };

  renderList = () => {
    const {
      isSearchable,
      searchByValue,
      wildcardSearch,
      dnd,
      emptyDropdownLabel,
      virtualizationProps,
      isOptionsLoading,
    } = this.props;

    if (isOptionsLoading) {
      return (
        <div className="d-flex align-items-center justify-content-center mb-4 mt-4">
          <ProgressCircle size={50} borderWidth={3} />
        </div>
      );
    }

    const { search, stateOfOptions } = this.state;
    let resultOptions = stateOfOptions;
    const res = [];

    if (stateOfOptions.length) {
      if (isSearchable) {
        resultOptions = filterItems(
          stateOfOptions,
          search.trimStart().trimEnd(),
          searchByValue,
          wildcardSearch,
        );
      }
      const cb = (item) => res.push(item);
      traverseState(resultOptions, cb);
    }
    if ((!res || !res.length) && emptyDropdownLabel) {
      return emptyDropdownLabel;
    }

    if (
      (!res || !res.length) &&
      isSearchable &&
      (search.trim().length || this.searchInput?.value.trim().length)
    ) {
      return (
        <div className="noResult">
          <Icon name="EmptyClipboard" /> <div className="mainTitle">Oops</div>
          <div className="message">No results found</div>
          <div className="subMessage">
            We can't find any items matching your search{' '}
            <div>"{search || this.searchInput?.value}"</div>
          </div>
        </div>
      );
    }

    if (!stateOfOptions) {
      return null;
    }

    if (virtualizationProps) {
      const selectListStyles = virtualizationProps
        ? {
            height: `${virtualizationProps.height}px`,
            maxHeight: `${virtualizationProps.height}px`,
            width: `${virtualizationProps.width}px`,
            maxWidth: `${virtualizationProps.width}px`,
          }
        : {};

      const isFixedSize = typeof virtualizationProps.itemSize === 'number';

      return (
        <div className="select__list" style={selectListStyles}>
          {isFixedSize ? (
            <FixedSizeList itemCount={res.length} {...virtualizationProps}>
              {this.getRenderVirtualizedItem(res, virtualizationProps.itemSize)}
            </FixedSizeList>
          ) : (
            <VariableSizeList
              ref={this.virtualizedVariableSizeListRef}
              itemCount={res.length}
              {...virtualizationProps}
              itemSize={(index) => virtualizationProps.itemSize(res[index])}
            >
              {this.getRenderVirtualizedItem(res, virtualizationProps.itemSize)}
            </VariableSizeList>
          )}
        </div>
      );
    }

    return dnd ? (
      <SortableContainer
        helperClass="select__dragged _active"
        distance={5}
        onSortStart={this.onSortStart}
        onSortEnd={this.onSortEnd}
        onAttachedRef={(el) => {
          this._selectList = el;
        }}
        items={res}
      >
        {res.map((item, i) => this.renderItem(item, i))}
      </SortableContainer>
    ) : (
      <div
        className={`select__list`}
        ref={(el) => {
          this._selectList = el;
        }}
      >
        {res.map((item, i) => this.renderItem(item, i))}
      </div>
    );
  };

  renderButton = (shouldRenderBeforeSearch = false) => {
    const { buttonProps } = this.props;

    if (buttonProps && shouldRenderBeforeSearch === buttonProps.shouldRenderBeforeSearch) {
      const onClick = buttonProps.shouldCloseOnClick
        ? () => {
            this.toggleSelect();
            buttonProps.onClick();
          }
        : buttonProps.onClick;

      return (
        <div className="w-100 d-flex justify-content-center">
          <Button
            className={buttonProps.className || 'btn-round _conflower-blue mt-3 mb-3'}
            onClick={onClick}
          >
            {!!buttonProps.symbolBeforeLabel && (
              <span className="btn-round__prefix">{buttonProps.symbolBeforeLabel}</span>
            )}
            {buttonProps.label || 'New'}
          </Button>
        </div>
      );
    }
    return null;
  };

  renderSelectedValue = () => {
    const { isMulti, multiPlaceholder, allSelectedText, value: v } = this.props;
    const { isOpen } = this.state;
    if (isMulti) {
      if (!isOpen && this.allSelected()) {
        return allSelectedText || 'All Selected';
      }
      if (v && !!v.length) {
        const selectionMap = {};
        const selectedCount = v.reduce((selectedCount, item) => {
          if (!item.uncountable) {
            selectedCount = selectionMap[item.value] ? selectedCount : selectedCount + 1;

            selectionMap[item.value] = item.value;
          }

          return selectedCount;
        }, 0);

        return multiPlaceholder ? multiPlaceholder(selectedCount, v) : `${selectedCount} Selected`;
      }
    } else {
      return v && (v.reactLabel || v.label);
    }
  };

  renderTabs = () => {
    const { value, getTabIcon } = this.props;
    const { tabsModeIsInclude } = this.state;
    return (
      <div className="select__tabs d-flex  mb-3">
        <div
          className={`tab d-flex flex-row justify-content-center align-items-center flex-grow-1 text-center ${
            tabsModeIsInclude ? '_active' : ''
          }`}
          onClick={() => this.setState({ tabsModeIsInclude: true })}
        >
          {`Allowlist (${
            Array.isArray(value) ? this.props.value.filter((i) => i.included).length : 0
          })`}
          {getTabIcon?.('include')}
        </div>
        <div
          className={`tab d-flex flex-row justify-content-center align-items-center flex-grow-1 text-center ${
            tabsModeIsInclude ? '' : '_active'
          }`}
          onClick={() => this.setState({ tabsModeIsInclude: false })}
        >
          {`Blocklist (${
            Array.isArray(value) ? this.props.value.filter((i) => i.excluded).length : 0
          })`}
          {getTabIcon?.('exclude')}
        </div>
      </div>
    );
  };

  content = () => {
    const {
      isMulti,
      tumbler,
      tumblerLabel,
      isSearchable,
      changeSearchHandler,
      withTabs,
      searchPlaceholder,
      portal,
      isTumblerDisabled = false,
    } = this.props;
    const { search, tumblerIsActive, xycoords } = this.state;
    return (
      <div
        className={`select__list-wrapper`}
        style={portal ? { position: 'fixed', display: 'block', ...xycoords } : undefined}
      >
        {withTabs && isMulti && this.renderTabs()}
        {this.renderButton(true)}
        {isSearchable && (
          <div className="select__list-input-wrapper">
            <Icon name="Search" />
            <input
              ref={(el) => {
                this.searchInput = el;
              }}
              placeholder={searchPlaceholder || 'Search'}
              className="select__list-input"
              value={changeSearchHandler ? undefined : search}
              onChange={
                changeSearchHandler
                  ? (event) => changeSearchHandler(event.target.value)
                  : this.handleChangeSearch
              }
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
            />
          </div>
        )}
        {this.renderButton()}
        {tumbler && (
          <Tumbler
            className="select__tumbler"
            onChange={this.toggleTumbler}
            on={tumblerIsActive}
            title={tumblerLabel || 'Show groups'}
            disabled={isTumblerDisabled}
          />
        )}
        {this.renderList()}
      </div>
    );
  };

  portalContent = () => {
    const { isOpen } = this.state;
    const contentContainer = (
      <div
        className={this.props.portalClassName ?? ''}
        style={{
          position: 'fixed',
          inset: 0,
          display: isOpen ? 'block' : 'none',
          zIndex: 9999, // We want it higher - refactor in future to have each popup component i.e tooltip to have a priority z-index.
        }}
      >
        <div
          className="select__portal"
          style={{
            display: isOpen ? 'block' : 'none',
          }}
        >
          {this.content()}
        </div>
      </div>
    );
    return ReactDOM.createPortal(contentContainer, document.body);
  };

  render() {
    const {
      className,
      label,
      reactLabel,
      allSelectable,
      isMulti,
      isClearable,
      hideCrossIfClosed,
      hideValueOnFocus,
      selectAllLabel,
      dnd,
      value,
      placeholder = 'Select...',
      tooltipParams,
      isAlwaysOpen,
      renderMenuTarget = () => {},
      isMenu,
      isReadOnly = false,
      portal,
      virtualizationProps,
      hideSelectAllWhenEmpty,
    } = this.props;
    const { isOpen, withGroups, isSorting, stateOfOptions } = this.state;

    const isSelected = value && (value.reactLabel || value.label || value.length);

    let labelClassName = 'select__label';
    if (hideValueOnFocus) {
      if (!isOpen) {
        if (isSelected) {
          labelClassName += ' _up';
        }
      }
    } else {
      if (isSelected) {
        labelClassName += ' _up';
      }
    }

    let showLabel = true;
    if (!reactLabel && !label) {
      if (!(hideValueOnFocus && isOpen) && isSelected) {
        showLabel = false;
      }
    }

    const classNames = ['select', 'js-select'];
    if (isSorting) {
      classNames.push('_sorting');
    }
    if (isAlwaysOpen || isOpen) {
      classNames.push('_opened');
    }
    if (withGroups) {
      classNames.push('_with-groups');
    }
    if (className) {
      classNames.push(className);
    }
    if (dnd) {
      classNames.push('_dnd');
    }

    const dropDownContent = portal ? this.portalContent() : this.content();

    if (virtualizationProps) {
      classNames.push('_virtualized');
    }

    const virtualizationStyles =
      virtualizationProps && isOpen
        ? {
            maxWidth: virtualizationProps.width,
            width: virtualizationProps.width,
          }
        : {};

    return (
      <>
        {isMenu && renderMenuTarget(this.toggleSelect, isOpen)}
        <div
          className={classNames.join(' ')}
          tabIndex={0}
          ref={(el) => {
            this.select = el;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || (!this.props.isSearchable && e.key === ' ')) {
              this.toggleSelect();
              e.stopPropagation();
            }
          }}
        >
          <div className="select__outer-wrapper">
            <div
              className="select__wrapper"
              style={virtualizationStyles}
              data-automation={kebabCase(placeholder || label)}
              ref={this.selectWrapperRef}
            >
              {!isMenu && (
                <BtnWrapper
                  className="select__btn"
                  handleClick={this.toggleSelect}
                  tooltipParams={tooltipParams}
                >
                  {showLabel && (
                    <div className={labelClassName}>
                      {hideValueOnFocus && isOpen
                        ? label || placeholder
                        : isSelected
                        ? label
                        : placeholder}
                    </div>
                  )}
                  <div
                    className={`select__value ${hideValueOnFocus ? '_bold' : ''} ${
                      isSelected ? '_selected' : ''
                    }`}
                  >
                    {(!hideValueOnFocus || !isOpen) && this.renderSelectedValue()}
                  </div>
                  {isMulti &&
                    isOpen &&
                    allSelectable &&
                    !this.allVisibleItemsSelected() &&
                    (!hideSelectAllWhenEmpty || !!stateOfOptions?.length) && (
                      <div
                        className="select__btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          this.selectAll();
                        }}
                      >
                        {selectAllLabel || 'Select All'}
                      </div>
                    )}
                  {(isClearable || allSelectable) &&
                    (!hideCrossIfClosed || isOpen) &&
                    !isReadOnly &&
                    value &&
                    (!Array.isArray(value) || !!value.length) && (
                      <div className="select__clear" onClick={this.handleClearValue}>
                        <Icon name="Close" />
                      </div>
                    )}
                  {!isReadOnly && <div className="select__indicator" />}
                </BtnWrapper>
              )}
              {(isAlwaysOpen || isOpen) && dropDownContent}
            </div>
          </div>
        </div>
      </>
    );
  }
}

BtnWrapper.propTypes = {
  className: PropTypes.string,
  tooltipParams: PropTypes.object,
  handleClick: PropTypes.func,
};

Select.defaultProps = {
  underline: false,
  portal: false,
  isOptionsLoading: false,
};

Select.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  isMulti: PropTypes.bool,
  tumblerIsActive: PropTypes.bool,
  multiPlaceholder: PropTypes.func,
  allSelectable: PropTypes.bool,
  hideSelectAllWhenEmpty: PropTypes.bool,
  isClearable: PropTypes.bool,
  hideCrossIfClosed: PropTypes.bool,
  hideValueOnFocus: PropTypes.bool,
  tumbler: PropTypes.bool,
  isTumblerDisabled: PropTypes.bool,
  dnd: PropTypes.bool,
  onTumblerChange: PropTypes.func,
  label: PropTypes.string,
  reactLabel: PropTypes.elementType,
  labelImageSrc: PropTypes.string,
  placeholder: PropTypes.string,
  selectAllLabel: PropTypes.string,
  tumblerLabel: PropTypes.string,
  isSearchable: PropTypes.bool,
  searchByValue: PropTypes.bool,
  wildcardSearch: PropTypes.bool,
  className: PropTypes.string,
  levelPadding: PropTypes.number,
  totalRecords: PropTypes.number,
  changeSearchHandler: PropTypes.func,
  allSelectedText: PropTypes.string,
  scrollableClassName: PropTypes.string,
  tooltipParams: PropTypes.object,
  isOpen: PropTypes.bool,
  toggleOpenProp: PropTypes.func,
  ignoredCloseClickElement: PropTypes.object,
  buttonProps: PropTypes.exact({
    label: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
    shouldCloseOnClick: PropTypes.bool,
    symbolBeforeLabel: PropTypes.string,
    shouldRenderBeforeSearch: PropTypes.bool,
  }),
  onSelectAll: PropTypes.func,
  withTabs: PropTypes.bool,
  getTabIcon: PropTypes.func,
  maxSelectedLimit: PropTypes.number,
  onExceededMaxLimit: PropTypes.func,
  withConfirmation: PropTypes.bool,
  isAlwaysOpen: PropTypes.bool,
  btnOnClick: PropTypes.func,
  isMenu: PropTypes.bool,
  renderMenuTarget: PropTypes.func,
  isReadOnly: PropTypes.bool,
  underline: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  emptyDropdownLabel: PropTypes.elementType,
  portal: PropTypes.bool,
  virtualizationProps: PropTypes.shape({
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    itemSize: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
    overscanCount: PropTypes.number,
  }),
  isOptionsLoading: PropTypes.bool,
  onClearAll: PropTypes.func,
  portalClassName: PropTypes.string,
};
